import React from 'react'
import { ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Text, LoadingSpinner, Tooltip, Input, Select, SortableTableHeader } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { modulesService } from '../../services/modules'
import {
  buildBrokerQuoteMaps,
  comercialCotacoesService,
  getBrokerRazao,
} from '../../services/comercialCotacoes'
import { comercialGestaoService } from '../../services/comercialGestao'
import type { ComercialUser } from '../../types/comercial'
import { userLogsService } from '../../services/userLogs'
import type { BrokerQuoteMaps, HelpdeskQuote } from '../../types/comercialCotacoes'
import {
  formatCurrencyBRL,
  formatDatePtBr,
  formatDateTimePtBr,
  formatIntegerPtBr,
  formatQuoteLabel,
  getClientDisplayName,
  formatResponsibleDisplayName,
  isPortalResponsible,
  normalizeResponsibleKey,
  normalizeStatusKey,
  quoteStatusBadgeClass,
  RESPONSAVEL_FILTER_CENTRAL,
  RESPONSAVEL_FILTER_PORTAL,
  type ResponsavelCotacaoOrigem,
  truncateWithTooltip,
} from '../../utils/cotacaoFormatters'
import { downloadXlsx } from '../../utils/downloadXlsx'
import CotacaoDetailModal from './CotacaoDetailModal'
import CotacoesDashboardTab from './CotacoesDashboardTab'
import { CheckboxMultiSelect } from '../Qualidade/CheckboxMultiSelect'
import { BrokerCombobox } from '../Qualidade/BrokerCombobox'
import { DASHBOARD_STATUS_ORDER } from '../../utils/comercialCotacoesDashboard'

const MODULE_NAME = 'comercial_cotacoes'
const ITEMS_PER_PAGE = 20
const COTACOES_LOG_ORIGEM = 'comercial/cotacoes'

function toDateInputValue(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Um mês civil para trás a partir de `date`.
 * Ex.: 07/08 → 07/07. Se o dia não existir no mês anterior (31→30, fev), usa o último dia válido.
 */
function subtractOneCalendarMonth(date: Date): Date {
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  const lastDayPrevMonth = new Date(year, month, 0).getDate()
  const useDay = Math.min(day, lastDayPrevMonth)
  return new Date(year, month - 1, useDay)
}

/** Período padrão: de (hoje − 1 mês civil) até hoje. */
function getDefaultDateRangeLastMonth(): { dataInicio: string; dataFim: string } {
  const end = new Date()
  const start = subtractOneCalendarMonth(end)
  return {
    dataInicio: toDateInputValue(start),
    dataFim: toDateInputValue(end),
  }
}

const DEFAULT_DATE_RANGE = getDefaultDateRangeLastMonth()

const STATUS_FILTER_OPTIONS = DASHBOARD_STATUS_ORDER.map(({ key, label }) => ({
  value: key,
  label,
}))

type CotacaoPdfDownloadSource = 'relacao' | 'modal'

async function logCotacaoPdfDownload(
  quote: HelpdeskQuote,
  source: CotacaoPdfDownloadSource
): Promise<void> {
  const quoteLabel = formatQuoteLabel(quote.quote_number, quote.version)
  try {
    await userLogsService.logUserAction({
      action: 'export',
      entityType: 'other',
      entityId: quote.id,
      description: `Baixou PDF da cotação ${quoteLabel}`,
      metadata: {
        origem: COTACOES_LOG_ORIGEM,
        additional_info:
          source === 'modal'
            ? 'Módulo Comercial • Cotações • Modal de detalhe'
            : 'Módulo Comercial • Cotações • Relação',
        quote_id: quote.id,
        quote_number: quote.quote_number,
        version: quote.version,
        contexto: source,
      },
    })
  } catch (logError) {
    console.warn('Erro ao registrar log de download de cotação:', logError)
  }
}

async function logCotacaoExcelExport(
  rowCount: number,
  filters: {
    cotacao: string
    cliente: string
    corretor: string
    status: string[]
    responsavelOrigem: ResponsavelCotacaoOrigem
    responsavelPessoas: string[]
    comercialUserId: string
    consultantUserId: string
    dataInicio: string
    dataFim: string
    statusHistorico: string
    statusHistoricoInicio: string
    statusHistoricoFim: string
  }
): Promise<void> {
  try {
    await userLogsService.logUserAction({
      action: 'export',
      entityType: 'other',
      description: `Comercial cotações: exportou relação (${rowCount} cotação(ões)).`,
      metadata: {
        origem: COTACOES_LOG_ORIGEM,
        row_count: rowCount,
        filters,
      },
    })
  } catch (logError) {
    console.warn('Erro ao registrar log de exportação Excel:', logError)
  }
}

type ActiveTab = 'relacao' | 'dashboard'

const tabClass = (activeTab: ActiveTab, tab: ActiveTab) =>
  `py-3 px-1 border-b-2 font-medium text-sm ${
    activeTab === tab
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  const maybe = error as { message?: string; code?: string }
  if (maybe?.message) return maybe.message
  if (maybe?.code) return `Erro (${maybe.code})`
  return 'Erro inesperado ao carregar cotações.'
}

const ComercialCotacoes: React.FC = () => {
  const { profile } = useAuthContext()
  const { hasPermission: canViewValues, loading: loadingViewValues } = usePermission(
    MODULE_NAME,
    'view_values'
  )
  const { hasPermission: canDownloadInfo } = usePermission(MODULE_NAME, 'download_info')
  const loggedUserId = profile?.id?.trim() ?? ''
  /** Só restringe depois de saber a permissão — trava comercial responsável (QG). */
  const isComercialFilterRestricted = !loadingViewValues && !canViewValues

  const [loading, setLoading] = React.useState(false)
  const [comercialUsers, setComercialUsers] = React.useState<ComercialUser[]>([])
  const [loadingComercialUsers, setLoadingComercialUsers] = React.useState(false)
  const [seedResponsibleOptions, setSeedResponsibleOptions] = React.useState<string[]>([])
  const [loadingQuoteResponsibles, setLoadingQuoteResponsibles] = React.useState(false)
  const [hasSearched, setHasSearched] = React.useState(false)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [quotes, setQuotes] = React.useState<HelpdeskQuote[]>([])
  const [brokerMaps, setBrokerMaps] = React.useState<BrokerQuoteMaps>({
    razaoById: new Map(),
    nomeById: new Map(),
  })
  const [brokerComercialByBrokerId, setBrokerComercialByBrokerId] = React.useState<
    Map<string, { comercialUserId: string; name: string }>
  >(() => new Map())
  const [brokerConsultantByBrokerId, setBrokerConsultantByBrokerId] = React.useState<
    Map<string, { consultantUserId: string; name: string }>
  >(() => new Map())
  const [moduleInfo, setModuleInfo] = React.useState<{
    display_name: string
    description?: string | null
  } | null>(null)
  const [currentPage, setCurrentPage] = React.useState(1)
  const [selectedQuote, setSelectedQuote] = React.useState<HelpdeskQuote | null>(null)
  const [pdfLoadingId, setPdfLoadingId] = React.useState<string | null>(null)
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('relacao')
  const [filtroCotacao, setFiltroCotacao] = React.useState('')
  const [filtroCliente, setFiltroCliente] = React.useState('')
  const [filtroBrokerId, setFiltroBrokerId] = React.useState<string | null>(null)
  const [filtroBroker, setFiltroBroker] = React.useState<{
    name: string
    id_scgp: number | null
  } | null>(null)
  const [filtroStatus, setFiltroStatus] = React.useState<string[]>([])
  const [filtroResponsavelOrigem, setFiltroResponsavelOrigem] =
    React.useState<ResponsavelCotacaoOrigem>('all')
  const [filtroResponsavelPessoas, setFiltroResponsavelPessoas] = React.useState<string[]>([])
  const [filtroComercialUserId, setFiltroComercialUserId] = React.useState('all')
  const [filtroConsultantUserId, setFiltroConsultantUserId] = React.useState('all')
  const [filtroDataInicio, setFiltroDataInicio] = React.useState(DEFAULT_DATE_RANGE.dataInicio)
  const [filtroDataFim, setFiltroDataFim] = React.useState(DEFAULT_DATE_RANGE.dataFim)
  const [filtroStatusHistorico, setFiltroStatusHistorico] = React.useState('')
  const [filtroStatusHistoricoInicio, setFiltroStatusHistoricoInicio] = React.useState('')
  const [filtroStatusHistoricoFim, setFiltroStatusHistoricoFim] = React.useState('')
  const [statusHistoryFilterError, setStatusHistoryFilterError] = React.useState<string | null>(null)
  const [appliedFilters, setAppliedFilters] = React.useState({
    cotacao: '',
    cliente: '',
    corretor: '',
    status: [] as string[],
    responsavelOrigem: 'all' as ResponsavelCotacaoOrigem,
    responsavelPessoas: [] as string[],
    comercialUserId: 'all',
    consultantUserId: 'all',
    dataInicio: DEFAULT_DATE_RANGE.dataInicio,
    dataFim: DEFAULT_DATE_RANGE.dataFim,
    statusHistorico: '',
    statusHistoricoInicio: '',
    statusHistoricoFim: '',
  })
  const [sortKey, setSortKey] = React.useState('inclusion_at')
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc')
  const [exportingExcel, setExportingExcel] = React.useState(false)
  const tableScrollRef = React.useRef<HTMLDivElement | null>(null)

  const scrollTableBy = React.useCallback((dx: number) => {
    tableScrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' })
  }, [])

  const handleTableWheel = React.useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      tableScrollRef.current?.scrollBy({ left: e.deltaY, behavior: 'auto' })
    }
  }, [])

  /** Nomes individuais do banco (sem Portal — fica como opção de grupo). */
  const individualResponsibleOptions = React.useMemo(() => {
    const byKey = new Map<string, string>()

    const addName = (raw: string) => {
      const trimmed = raw.trim()
      if (!trimmed) return
      if (isPortalResponsible(trimmed)) return
      const key = normalizeResponsibleKey(trimmed)
      if (!key || byKey.has(key)) return
      byKey.set(key, formatResponsibleDisplayName(trimmed))
    }

    for (const name of seedResponsibleOptions) {
      addName(name)
    }
    for (const quote of quotes) {
      if (quote.responsible) addName(quote.responsible)
    }

    return Array.from(byKey.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
  }, [seedResponsibleOptions, quotes])

  const displayedComercialUserId = isComercialFilterRestricted
    ? loggedUserId || 'all'
    : filtroComercialUserId

  const getBrokerComercial = React.useCallback(
    (brokerId: string | null | undefined) => {
      if (!brokerId) return null
      return brokerComercialByBrokerId.get(brokerId) ?? null
    },
    [brokerComercialByBrokerId]
  )

  const getBrokerConsultant = React.useCallback(
    (brokerId: string | null | undefined) => {
      if (!brokerId) return null
      return brokerConsultantByBrokerId.get(brokerId) ?? null
    },
    [brokerConsultantByBrokerId]
  )

  const buildListFilters = React.useCallback(
    (filters: typeof appliedFilters) => {
      const comercialUserId = isComercialFilterRestricted
        ? loggedUserId || null
        : filters.comercialUserId === 'all'
          ? null
          : filters.comercialUserId

      return {
        cotacao: filters.cotacao,
        cliente: filters.cliente,
        corretor: filters.corretor,
        status: filters.status,
        responsavelOrigem: filters.responsavelOrigem,
        responsavelPessoas:
          filters.responsavelOrigem === RESPONSAVEL_FILTER_CENTRAL
            ? filters.responsavelPessoas
            : [],
        comercialUserId,
        dataInicio: filters.dataInicio,
        dataFim: filters.dataFim,
        statusHistorico: filters.statusHistorico,
        statusHistoricoInicio: filters.statusHistoricoInicio,
        statusHistoricoFim: filters.statusHistoricoFim,
      }
    },
    [isComercialFilterRestricted, loggedUserId]
  )

  const loadData = React.useCallback(
    async (filters: typeof appliedFilters) => {
      setLoading(true)
      setPageError(null)
      try {
        const quotesData = await comercialCotacoesService.listQuotes(buildListFilters(filters))
        const brokerIds = Array.from(
          new Set(quotesData.map((q) => q.broker_id).filter((id): id is string => !!id))
        )
        const [brokersData, assignmentMap, consultantAssignmentMap] = await Promise.all([
          comercialCotacoesService.listBrokersLookupByIds(brokerIds),
          comercialGestaoService
            .getBrokerComercialAssignmentMapForBrokerIds(brokerIds)
            .catch((err) => {
              console.warn('Erro ao carregar mapa de comercial por corretor:', err)
              return new Map<string, { comercialUserId: string; name: string }>()
            }),
          comercialGestaoService
            .getBrokerConsultantAssignmentMapForBrokerIds(brokerIds)
            .catch((err) => {
              console.warn('Erro ao carregar mapa de consultor por corretor:', err)
              return new Map<string, { consultantUserId: string; name: string }>()
            }),
        ])
        setQuotes(quotesData)
        setBrokerMaps(buildBrokerQuoteMaps(brokersData))
        setBrokerComercialByBrokerId(assignmentMap)
        setBrokerConsultantByBrokerId(consultantAssignmentMap)
      } catch (error) {
        console.error('Erro ao carregar cotações:', error)
        setPageError(getErrorMessage(error))
        setQuotes([])
        setBrokerComercialByBrokerId(new Map())
        setBrokerConsultantByBrokerId(new Map())
      } finally {
        setLoading(false)
      }
    },
    [buildListFilters]
  )

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName(MODULE_NAME)
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name ?? 'Cotações',
          description: result.data.description ?? null,
        })
      }
    }
    void loadModule()
  }, [])

  React.useEffect(() => {
    setLoadingComercialUsers(true)
    void comercialGestaoService
      .listComercialUsers()
      .then(setComercialUsers)
      .catch(() => setComercialUsers([]))
      .finally(() => setLoadingComercialUsers(false))
  }, [])

  React.useEffect(() => {
    setLoadingQuoteResponsibles(true)
    void comercialCotacoesService
      .listDistinctQuoteResponsibles()
      .then(setSeedResponsibleOptions)
      .catch((err) => {
        console.warn('Erro ao carregar responsáveis da cotação:', err)
        setSeedResponsibleOptions([])
      })
      .finally(() => setLoadingQuoteResponsibles(false))
  }, [])

  React.useEffect(() => {
    if (loadingViewValues) return
    if (!canViewValues && loggedUserId) {
      setFiltroComercialUserId(loggedUserId)
    }
  }, [loadingViewValues, canViewValues, loggedUserId])

  /** Resultado filtrado no servidor (comercial etc.) + consultor no cliente. */
  const filteredQuotes = React.useMemo(() => {
    const consultantId = appliedFilters.consultantUserId
    if (!consultantId || consultantId === 'all') return quotes
    return quotes.filter((quote) => {
      if (!quote.broker_id) return false
      return brokerConsultantByBrokerId.get(quote.broker_id)?.consultantUserId === consultantId
    })
  }, [quotes, appliedFilters.consultantUserId, brokerConsultantByBrokerId])

  const hasStatusHistoryFields = !!(
    filtroStatusHistorico.trim() ||
    filtroStatusHistoricoInicio ||
    filtroStatusHistoricoFim
  )
  const isStatusHistoryFilterComplete = !!(
    filtroStatusHistorico.trim() &&
    (filtroStatusHistoricoInicio || filtroStatusHistoricoFim)
  )

  const hasFiltersActive = React.useMemo(() => {
    if (isComercialFilterRestricted && loggedUserId) return true
    return !!(
      filtroCotacao.trim() ||
      filtroCliente.trim() ||
      filtroBrokerId ||
      filtroStatus.length > 0 ||
      filtroResponsavelOrigem !== 'all' ||
      filtroResponsavelPessoas.length > 0 ||
      filtroComercialUserId !== 'all' ||
      filtroConsultantUserId !== 'all' ||
      filtroDataInicio ||
      filtroDataFim ||
      hasStatusHistoryFields
    )
  }, [
    isComercialFilterRestricted,
    loggedUserId,
    filtroCotacao,
    filtroCliente,
    filtroBrokerId,
    filtroStatus,
    filtroResponsavelOrigem,
    filtroResponsavelPessoas,
    filtroComercialUserId,
    filtroConsultantUserId,
    filtroDataInicio,
    filtroDataFim,
    hasStatusHistoryFields,
  ])

  const applyFilters = () => {
    if (!hasFiltersActive) return

    if (hasStatusHistoryFields && !isStatusHistoryFilterComplete) {
      setStatusHistoryFilterError('Escolha o status e ao menos uma data do período.')
      return
    }
    setStatusHistoryFilterError(null)

    const nextFilters = {
      cotacao: filtroCotacao,
      cliente: filtroCliente,
      corretor: filtroBroker?.name?.trim() || '',
      status: filtroStatus,
      responsavelOrigem: filtroResponsavelOrigem,
      responsavelPessoas:
        filtroResponsavelOrigem === RESPONSAVEL_FILTER_CENTRAL ? filtroResponsavelPessoas : [],
      comercialUserId: isComercialFilterRestricted ? loggedUserId || 'all' : filtroComercialUserId,
      consultantUserId: filtroConsultantUserId,
      dataInicio: filtroDataInicio,
      dataFim: filtroDataFim,
      statusHistorico: isStatusHistoryFilterComplete ? filtroStatusHistorico.trim() : '',
      statusHistoricoInicio: isStatusHistoryFilterComplete ? filtroStatusHistoricoInicio : '',
      statusHistoricoFim: isStatusHistoryFilterComplete ? filtroStatusHistoricoFim : '',
    }
    setAppliedFilters(nextFilters)
    setCurrentPage(1)
    setHasSearched(true)
    void loadData(nextFilters)
  }

  const toggleSort = React.useCallback((key: string) => {
    if (sortKey === key) {
      setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
    setCurrentPage(1)
  }, [sortKey])

  const sortedQuotes = React.useMemo(() => {
    return [...filteredQuotes].sort((a, b) => {
      if (sortKey === 'cotacao') {
        const numDiff = (a.quote_number ?? 0) - (b.quote_number ?? 0)
        if (numDiff !== 0) return sortOrder === 'asc' ? numDiff : -numDiff
        const verDiff = (a.version ?? 0) - (b.version ?? 0)
        return sortOrder === 'asc' ? verDiff : -verDiff
      }

      let av: string | number = ''
      let bv: string | number = ''

      switch (sortKey) {
        case 'cliente':
          av = getClientDisplayName(a).toLowerCase()
          bv = getClientDisplayName(b).toLowerCase()
          break
        case 'corretor':
          av = getBrokerRazao(a.broker_id, brokerMaps).toLowerCase()
          bv = getBrokerRazao(b.broker_id, brokerMaps).toLowerCase()
          break
        case 'responsavel_cotacao':
          av = normalizeResponsibleKey(a.responsible)
          bv = normalizeResponsibleKey(b.responsible)
          break
        case 'comercial_responsavel':
          av = (a.broker_id ? brokerComercialByBrokerId.get(a.broker_id)?.name : '') ?? ''
          bv = (b.broker_id ? brokerComercialByBrokerId.get(b.broker_id)?.name : '') ?? ''
          av = String(av).toLowerCase()
          bv = String(bv).toLowerCase()
          break
        case 'consultant_responsavel':
          av = (a.broker_id ? brokerConsultantByBrokerId.get(a.broker_id)?.name : '') ?? ''
          bv = (b.broker_id ? brokerConsultantByBrokerId.get(b.broker_id)?.name : '') ?? ''
          av = String(av).toLowerCase()
          bv = String(bv).toLowerCase()
          break
        case 'vidas':
          av = a.insured_count ?? 0
          bv = b.insured_count ?? 0
          break
        case 'premio':
          av = a.amount_per_life ?? 0
          bv = b.amount_per_life ?? 0
          break
        case 'status':
          av = normalizeStatusKey(a.status)
          bv = normalizeStatusKey(b.status)
          break
        case 'obs_count':
          av = a.obs_count ?? 0
          bv = b.obs_count ?? 0
          break
        case 'last_obs_at': {
          av = a.last_created_at ? new Date(a.last_created_at).getTime() : 0
          bv = b.last_created_at ? new Date(b.last_created_at).getTime() : 0
          break
        }
        case 'inclusion_at':
        default:
          av = a.inclusion_at ?? ''
          bv = b.inclusion_at ?? ''
          break
      }

      if (av < bv) return sortOrder === 'asc' ? -1 : 1
      if (av > bv) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredQuotes, sortKey, sortOrder, brokerMaps, brokerComercialByBrokerId, brokerConsultantByBrokerId])

  const limparFiltros = () => {
    const range = getDefaultDateRangeLastMonth()
    setFiltroCotacao('')
    setFiltroCliente('')
    setFiltroBrokerId(null)
    setFiltroBroker(null)
    setFiltroStatus([])
    setFiltroResponsavelOrigem('all')
    setFiltroResponsavelPessoas([])
    if (!isComercialFilterRestricted) {
      setFiltroComercialUserId('all')
    } else if (loggedUserId) {
      setFiltroComercialUserId(loggedUserId)
    }
    setFiltroConsultantUserId('all')
    setFiltroDataInicio(range.dataInicio)
    setFiltroDataFim(range.dataFim)
    setFiltroStatusHistorico('')
    setFiltroStatusHistoricoInicio('')
    setFiltroStatusHistoricoFim('')
    setStatusHistoryFilterError(null)
    setAppliedFilters({
      cotacao: '',
      cliente: '',
      corretor: '',
      status: [],
      responsavelOrigem: 'all',
      responsavelPessoas: [],
      comercialUserId: isComercialFilterRestricted && loggedUserId ? loggedUserId : 'all',
      consultantUserId: 'all',
      dataInicio: range.dataInicio,
      dataFim: range.dataFim,
      statusHistorico: '',
      statusHistoricoInicio: '',
      statusHistoricoFim: '',
    })
    setHasSearched(false)
    setQuotes([])
    setCurrentPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(sortedQuotes.length / ITEMS_PER_PAGE))
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedQuotes = sortedQuotes.slice(startIndex, endIndex)

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const handleDownloadPdf = React.useCallback(
    async (quote: HelpdeskQuote, source: CotacaoPdfDownloadSource = 'relacao') => {
      if (!canDownloadInfo) return
      setPdfLoadingId(quote.id)
      try {
        await comercialCotacoesService.downloadQuotePdf(quote)
        await logCotacaoPdfDownload(quote, source)
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Não foi possível baixar o PDF.'
        window.alert(message)
      } finally {
        setPdfLoadingId(null)
      }
    },
    [canDownloadInfo]
  )

  const openQuoteDetail = (quote: HelpdeskQuote) => {
    setSelectedQuote(quote)
  }

  const handleRowKeyDown = (event: React.KeyboardEvent, quote: HelpdeskQuote) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openQuoteDetail(quote)
    }
  }

  const exportRelacaoExcel = () => {
    if (!canDownloadInfo || !hasSearched || sortedQuotes.length === 0 || exportingExcel) return

    const rows = sortedQuotes.map((quote) => {
      const comercial = getBrokerComercial(quote.broker_id)
      const consultant = getBrokerConsultant(quote.broker_id)
      return {
        Cotação: formatQuoteLabel(quote.quote_number, quote.version),
        Cliente: getClientDisplayName(quote),
        Corretor: getBrokerRazao(quote.broker_id, brokerMaps),
        Vidas: quote.insured_count ?? '',
        'Prêmio/Vida': quote.amount_per_life ?? '',
        Status: quote.status ?? '',
        'Data inclusão': formatDatePtBr(quote.inclusion_at),
        Comentários: quote.obs_count ?? 0,
        'Último comentário': quote.last_created_at
          ? formatDateTimePtBr(quote.last_created_at)
          : '',
        'Comercial responsável corretor': comercial?.name ?? '',
        'Consultor responsável corretor': consultant?.name ?? '',
        'Responsável cotação': quote.responsible ?? '',
      }
    })

    setExportingExcel(true)
    setPageError(null)
    try {
      const dateStamp = new Date().toISOString().slice(0, 10)
      downloadXlsx('Relação', `cotacoes-${dateStamp}.xlsx`, rows)
      void logCotacaoExcelExport(sortedQuotes.length, appliedFilters)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setExportingExcel(false)
    }
  }

  const tableColSpan = canDownloadInfo ? 13 : 12

  const selectedQuoteComercialNome =
    selectedQuote?.broker_id
      ? brokerComercialByBrokerId.get(selectedQuote.broker_id)?.name ?? null
      : null

  const selectedQuoteConsultantNome =
    selectedQuote?.broker_id
      ? brokerConsultantByBrokerId.get(selectedQuote.broker_id)?.name ?? null
      : null

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard moduleName={MODULE_NAME} permissionType="show">
          <div className="h-full flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleInfo?.display_name ?? 'Cotações'}
              </Heading>
              {moduleInfo?.description && (
                <Text size="sm" color="muted" className="mt-1">
                  {moduleInfo.description}
                </Text>
              )}
            </div>

            <div className="px-6 py-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Filtros
                  </Heading>
                </div>

                <div className="mb-6 space-y-4">
                  <Text
                    size="xs"
                    className="text-gray-500 dark:text-gray-400 uppercase tracking-wide font-bold pb-2 border-b border-gray-200 dark:border-gray-700 block"
                  >
                    Dados da cotação
                  </Text>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-end">
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Cotação
                      </Text>
                      <Input
                        placeholder="Número ou versão..."
                        value={filtroCotacao}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFiltroCotacao(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Cliente
                      </Text>
                      <Input
                        placeholder="Empresa ou solicitante..."
                        value={filtroCliente}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFiltroCliente(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Corretor
                      </Text>
                      <BrokerCombobox
                        value={filtroBrokerId}
                        initialBroker={filtroBroker}
                        onChange={(id, broker) => {
                          setFiltroBrokerId(id)
                          setFiltroBroker(
                            broker
                              ? { name: broker.name, id_scgp: broker.id_scgp }
                              : null
                          )
                        }}
                        className="w-full"
                        placeholder="Digite o código SCGP ou nome"
                      />
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Comercial responsável corretor
                      </Text>
                      <Select
                        value={displayedComercialUserId}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          setFiltroComercialUserId(e.target.value)
                        }
                        disabled={loadingComercialUsers || isComercialFilterRestricted}
                        className="w-full"
                      >
                        {!isComercialFilterRestricted && <option value="all">Todos</option>}
                        {comercialUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                        {isComercialFilterRestricted &&
                          loggedUserId &&
                          !comercialUsers.some((u) => u.id === loggedUserId) && (
                            <option value={loggedUserId}>
                              {profile?.name?.trim() || 'Meu usuário'}
                            </option>
                          )}
                      </Select>
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Consultor responsável corretor
                      </Text>
                      <Select
                        value={filtroConsultantUserId}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          setFiltroConsultantUserId(e.target.value)
                        }
                        disabled={loadingComercialUsers}
                        className="w-full"
                      >
                        <option value="all">Todos</option>
                        {comercialUsers.map((user) => (
                          <option key={`consultant-${user.id}`} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Canal
                      </Text>
                      <Select
                        value={filtroResponsavelOrigem}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          const value = e.target.value as ResponsavelCotacaoOrigem
                          setFiltroResponsavelOrigem(value)
                          if (value !== RESPONSAVEL_FILTER_CENTRAL) {
                            setFiltroResponsavelPessoas([])
                          }
                        }}
                        className="w-full"
                      >
                        <option value="all">Todos</option>
                        <option value={RESPONSAVEL_FILTER_PORTAL}>Portal</option>
                        <option value={RESPONSAVEL_FILTER_CENTRAL}>Central</option>
                      </Select>
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Responsável Cotação
                      </Text>
                      <CheckboxMultiSelect
                        options={individualResponsibleOptions}
                        selected={
                          filtroResponsavelOrigem === RESPONSAVEL_FILTER_CENTRAL
                            ? filtroResponsavelPessoas
                            : []
                        }
                        onChange={setFiltroResponsavelPessoas}
                        placeholder="Todos da Central"
                        disabled={
                          filtroResponsavelOrigem !== RESPONSAVEL_FILTER_CENTRAL ||
                          (loadingQuoteResponsibles && individualResponsibleOptions.length === 0)
                        }
                        className="w-full"
                      />
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Status atual
                      </Text>
                      <CheckboxMultiSelect
                        options={STATUS_FILTER_OPTIONS}
                        selected={filtroStatus}
                        onChange={setFiltroStatus}
                        placeholder="Todos"
                        className="w-full"
                      />
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Data inclusão (de)
                      </Text>
                      <Input
                        type="date"
                        value={filtroDataInicio}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFiltroDataInicio(e.target.value)
                        }
                        className="w-full"
                      />
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Data inclusão (até)
                      </Text>
                      <Input
                        type="date"
                        value={filtroDataFim}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFiltroDataFim(e.target.value)
                        }
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-4 space-y-4">
                  <Text
                    size="xs"
                    className="text-gray-500 dark:text-gray-400 uppercase tracking-wide font-bold pb-2 border-b border-gray-200 dark:border-gray-700 block"
                  >
                    Mudança de status
                  </Text>
                  <Text size="xs" color="muted" className="block">
                    Cotações que passaram para o status escolhido entre as datas. Não usa o status atual
                    nem a data de inclusão.
                  </Text>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-end">
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Status da mudança
                      </Text>
                      <Select
                        value={filtroStatusHistorico}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          setFiltroStatusHistorico(e.target.value)
                          if (statusHistoryFilterError) setStatusHistoryFilterError(null)
                        }}
                        className="w-full"
                      >
                        <option value="">Selecione</option>
                        {STATUS_FILTER_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Período (de)
                      </Text>
                      <Input
                        type="date"
                        value={filtroStatusHistoricoInicio}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFiltroStatusHistoricoInicio(e.target.value)
                          if (statusHistoryFilterError) setStatusHistoryFilterError(null)
                        }}
                        className="w-full"
                      />
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Período (até)
                      </Text>
                      <Input
                        type="date"
                        value={filtroStatusHistoricoFim}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFiltroStatusHistoricoFim(e.target.value)
                          if (statusHistoryFilterError) setStatusHistoryFilterError(null)
                        }}
                        className="w-full"
                      />
                    </div>
                  </div>
                  {statusHistoryFilterError && (
                    <div
                      role="alert"
                      className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
                    >
                      {statusHistoryFilterError}
                    </div>
                  )}
                </div>

                <div className="mt-4 flex justify-end gap-2">
                  <Button
                    variant="solid"
                    size="sm"
                    onClick={applyFilters}
                    disabled={loading || !hasFiltersActive}
                    title={!hasFiltersActive ? 'Selecione ao menos um filtro para buscar' : undefined}
                  >
                    Buscar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={limparFiltros}
                    disabled={loading || (!hasSearched && !hasFiltersActive)}
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </div>
            </div>

            <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                  <button
                    type="button"
                    onClick={() => setActiveTab('relacao')}
                    className={tabClass(activeTab, 'relacao')}
                  >
                    Relação
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('dashboard')}
                    className={tabClass(activeTab, 'dashboard')}
                  >
                    Dashboard
                  </button>
                </nav>
              </div>
            </div>

            {pageError && (
              <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                <Text size="sm">{pageError}</Text>
              </div>
            )}

            {activeTab === 'dashboard' && (
              <CotacoesDashboardTab
                quotes={filteredQuotes}
                loading={loading}
                hasSearched={hasSearched}
                dataInicio={appliedFilters.dataInicio}
                dataFim={appliedFilters.dataFim}
                onQuoteClick={openQuoteDetail}
              />
            )}

            {activeTab === 'relacao' && (
            <>
            <div className="flex-1 px-6 py-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden flex flex-col h-full min-h-0">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                    Cotações
                    <span className="ml-2 text-base font-normal text-gray-500 dark:text-gray-400">
                      ({sortedQuotes.length} registros)
                    </span>
                  </Heading>
                  {canDownloadInfo && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportRelacaoExcel}
                    disabled={!hasSearched || loading || sortedQuotes.length === 0 || exportingExcel}
                    title={
                      !hasSearched
                        ? 'Busque as cotações antes de exportar'
                        : sortedQuotes.length === 0
                          ? 'Nenhum registro para exportar'
                          : 'Baixar Excel com a relação filtrada'
                    }
                  >
                    {exportingExcel ? (
                      <span className="inline-flex items-center gap-1.5">
                        <LoadingSpinner size="sm" />
                        Exportando...
                      </span>
                    ) : (
                      <>
                        <ArrowDownTrayIcon className="h-4 w-4 mr-1.5 inline" aria-hidden />
                        Baixar Excel
                      </>
                    )}
                  </Button>
                  )}
                </div>
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => scrollTableBy(-320)}
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                    aria-label="Rolar para a esquerda"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollTableBy(320)}
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                    aria-label="Rolar para a direita"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex-1 overflow-auto min-h-0">
                  <div
                    ref={tableScrollRef}
                    className="overflow-x-auto h-full"
                    onWheel={handleTableWheel}
                  >
                  <table className="w-full min-w-max divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10 text-xs text-gray-500 dark:text-gray-400 uppercase">
                      <tr>
                        <SortableTableHeader sortKey="cotacao" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} compact className="px-4 py-3">
                          Cotação
                        </SortableTableHeader>
                        <SortableTableHeader sortKey="inclusion_at" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} compact className="px-4 py-3">
                          Data
                        </SortableTableHeader>
                        <SortableTableHeader sortKey="status" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} compact className="px-4 py-3">
                          Status
                        </SortableTableHeader>
                        <SortableTableHeader sortKey="cliente" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} compact className="px-4 py-3">
                          Cliente
                        </SortableTableHeader>
                        <SortableTableHeader sortKey="corretor" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} compact className="px-4 py-3">
                          Corretor
                        </SortableTableHeader>
                        <SortableTableHeader sortKey="comercial_responsavel" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} compact className="px-4 py-3 whitespace-normal">
                          <span className="block whitespace-normal leading-tight">
                            Comercial resp.
                            <br />
                            corretor
                          </span>
                        </SortableTableHeader>
                        <SortableTableHeader sortKey="consultant_responsavel" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} compact className="px-4 py-3 whitespace-normal">
                          <span className="block whitespace-normal leading-tight">
                            Consultor resp.
                            <br />
                            corretor
                          </span>
                        </SortableTableHeader>
                        <SortableTableHeader sortKey="responsavel_cotacao" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} compact className="px-4 py-3 whitespace-normal">
                          <span className="block whitespace-normal leading-tight">
                            Resp.
                            <br />
                            cotação
                          </span>
                        </SortableTableHeader>
                        <SortableTableHeader sortKey="vidas" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} compact className="px-4 py-3 text-right">
                          Vidas
                        </SortableTableHeader>
                        <SortableTableHeader sortKey="premio" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} compact className="px-4 py-3 text-right">
                          Prêmio/Vida
                        </SortableTableHeader>
                        <SortableTableHeader sortKey="obs_count" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} compact className="px-4 py-3 text-right">
                          Comentários
                        </SortableTableHeader>
                        <SortableTableHeader sortKey="last_obs_at" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} compact className="px-4 py-3">
                          Último comentário
                        </SortableTableHeader>
                        {canDownloadInfo && (
                        <th className="px-4 py-3 text-center font-medium w-12">
                          <span className="sr-only">Download PDF</span>
                        </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {!hasSearched ? (
                        <tr>
                          <td
                            colSpan={tableColSpan}
                            className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                          >
                            Selecione ao menos um filtro e clique em Buscar para carregar os registros.
                          </td>
                        </tr>
                      ) : loading ? (
                        <tr>
                          <td colSpan={tableColSpan} className="px-4 py-8 text-center">
                            <LoadingSpinner />
                          </td>
                        </tr>
                      ) : paginatedQuotes.length === 0 ? (
                        <tr>
                          <td
                            colSpan={tableColSpan}
                            className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                          >
                            Nenhuma cotação encontrada com os filtros aplicados.
                          </td>
                        </tr>
                      ) : (
                        paginatedQuotes.map((quote) => {
                          const clientName = getClientDisplayName(quote)
                          const clientTrunc = truncateWithTooltip(clientName)
                          const brokerName = getBrokerRazao(quote.broker_id, brokerMaps)
                          const brokerTrunc = truncateWithTooltip(brokerName)
                          const isPdfLoading = pdfLoadingId === quote.id
                          const obsCount = quote.obs_count ?? 0
                          const lastObsAt = quote.last_created_at
                          const responsavelCotacao = quote.responsible?.trim()
                            ? formatResponsibleDisplayName(quote.responsible)
                            : '—'
                          const comercialNome = getBrokerComercial(quote.broker_id)?.name ?? '—'
                          const consultantNome = getBrokerConsultant(quote.broker_id)?.name ?? '—'
                          const responsavelTrunc = truncateWithTooltip(responsavelCotacao)
                          const comercialTrunc = truncateWithTooltip(comercialNome)
                          const consultantTrunc = truncateWithTooltip(consultantNome)

                          return (
                            <tr
                              key={quote.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => openQuoteDetail(quote)}
                              onKeyDown={(event) => handleRowKeyDown(event, quote)}
                              className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-700/50"
                            >
                              <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                                {formatQuoteLabel(quote.quote_number, quote.version)}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                {formatDatePtBr(quote.inclusion_at)}
                              </td>
                              <td className="px-4 py-2">
                                {quote.status ? (
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${quoteStatusBadgeClass(quote.status)}`}
                                  >
                                    {quote.status}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-[180px]">
                                {clientTrunc.title ? (
                                  <Tooltip content={clientTrunc.title}>
                                    <span>{clientTrunc.display}</span>
                                  </Tooltip>
                                ) : (
                                  clientTrunc.display
                                )}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-[180px]">
                                {brokerTrunc.title ? (
                                  <Tooltip content={brokerTrunc.title}>
                                    <span>{brokerTrunc.display}</span>
                                  </Tooltip>
                                ) : (
                                  brokerTrunc.display
                                )}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-[140px]">
                                {comercialTrunc.title ? (
                                  <Tooltip content={comercialTrunc.title}>
                                    <span>{comercialTrunc.display}</span>
                                  </Tooltip>
                                ) : (
                                  comercialTrunc.display
                                )}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-[140px]">
                                {consultantTrunc.title ? (
                                  <Tooltip content={consultantTrunc.title}>
                                    <span>{consultantTrunc.display}</span>
                                  </Tooltip>
                                ) : (
                                  consultantTrunc.display
                                )}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-[140px]">
                                {responsavelTrunc.title ? (
                                  <Tooltip content={responsavelTrunc.title}>
                                    <span>{responsavelTrunc.display}</span>
                                  </Tooltip>
                                ) : (
                                  responsavelTrunc.display
                                )}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap text-right">
                                {formatIntegerPtBr(quote.insured_count)}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap text-right">
                                {formatCurrencyBRL(quote.amount_per_life)}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap text-right">
                                {formatIntegerPtBr(obsCount)}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                {lastObsAt ? formatDateTimePtBr(lastObsAt) : '—'}
                              </td>
                              {canDownloadInfo && (
                              <td className="px-4 py-2 text-center">
                                <Tooltip content={isPdfLoading ? 'Baixando...' : 'Baixar Cotação'}>
                                  <button
                                    type="button"
                                    disabled={isPdfLoading}
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      void handleDownloadPdf(quote, 'relacao')
                                    }}
                                    className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors disabled:opacity-50 min-w-[28px] min-h-[28px]"
                                    aria-label={
                                      isPdfLoading
                                        ? 'Baixando cotação...'
                                        : `Baixar PDF da cotação ${formatQuoteLabel(quote.quote_number, quote.version)}`
                                    }
                                    aria-busy={isPdfLoading}
                                  >
                                    {isPdfLoading ? (
                                      <LoadingSpinner size="sm" />
                                    ) : (
                                      <ArrowDownTrayIcon className="h-4 w-4" />
                                    )}
                                  </button>
                                </Tooltip>
                              </td>
                              )}
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>

                {!loading && hasSearched && totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300">
                        Mostrando {sortedQuotes.length === 0 ? 0 : startIndex + 1} a{' '}
                        {Math.min(endIndex, sortedQuotes.length)} de {sortedQuotes.length} registros
                      </Text>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                          disabled={currentPage === 1}
                        >
                          Anterior
                        </Button>
                        <Text size="sm" color="muted">
                          Página {currentPage} de {totalPages}
                        </Text>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Próxima
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </>
            )}
          </div>
        </PermissionGuard>

      {selectedQuote && (
        <CotacaoDetailModal
          quote={selectedQuote}
          brokerMaps={brokerMaps}
          comercialResponsavelNome={selectedQuoteComercialNome}
          consultantResponsavelNome={selectedQuoteConsultantNome}
          onClose={() => setSelectedQuote(null)}
          onDownloadPdf={handleDownloadPdf}
          pdfLoading={pdfLoadingId === selectedQuote.id}
          canDownloadInfo={canDownloadInfo}
        />
      )}
    </div>
  )
}

export default ComercialCotacoes
